// ─────────────────────────────────────────────────────────────
// ZIFEK — Média des résultats du chat IA
// Un résultat produit peut porter plusieurs images ET des vidéos
// (champs `media`, `image`, `videos`). Ce module normalise tout cela
// en une seule liste exploitable pour les cartes et la galerie.
// ─────────────────────────────────────────────────────────────

export interface ChatMediaItem {
  url: string;
  type: 'image' | 'video';
}

function detectType(url: string, hint?: string): 'image' | 'video' {
  if (hint === 'video' || hint === 'image') return hint;
  const clean = url.split('?')[0].toLowerCase();
  if (/\.(mp4|webm|ogg|mov|m4v)$/.test(clean)) return 'video';
  return 'image';
}

/** Ajoute une entrée à la liste en évitant les doublons et les URLs vides. */
function pushItem(list: ChatMediaItem[], seen: Set<string>, raw: unknown, hint?: string): void {
  if (!raw) return;
  let url = '';
  let typeHint = hint;
  if (typeof raw === 'string') {
    url = raw.trim();
  } else if (typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    url = String(obj.url ?? obj.src ?? obj.video_url ?? '').trim();
    if (typeof obj.type === 'string') typeHint = obj.type;
  }
  if (!url) return;
  if (seen.has(url)) return;
  seen.add(url);
  list.push({ url, type: detectType(url, typeHint) });
}

function parseMaybeJsonArray(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed;
      } catch {
        /* pas du JSON : on prend la chaîne telle quelle */
      }
    }
    if (trimmed) return [trimmed];
  }
  return [];
}

/**
 * Construit la liste complète des médias d'un résultat produit/service :
 * `media` (tableau mixte ou JSON), `image` (couverture) et `videos`.
 */
export function extractChatMedia(source: {
  image?: unknown;
  media?: unknown;
  videos?: unknown;
} | null | undefined): ChatMediaItem[] {
  if (!source) return [];
  const list: ChatMediaItem[] = [];
  const seen = new Set<string>();

  // 1) Média principal (tableau JSONB mixte images/vidéos).
  for (const entry of parseMaybeJsonArray(source.media)) {
    pushItem(list, seen, entry);
  }
  // 2) Image de couverture explicite.
  pushItem(list, seen, source.image, 'image');
  // 3) Vidéos séparées.
  for (const entry of parseMaybeJsonArray(source.videos)) {
    pushItem(list, seen, entry, 'video');
  }

  // Les images d'abord, puis les vidéos (ordre d'affichage naturel).
  return [
    ...list.filter((m) => m.type === 'image'),
    ...list.filter((m) => m.type === 'video'),
  ];
}

/** Première image exploitable (pour une vignette de carte). */
export function firstChatImage(source: {
  image?: unknown;
  media?: unknown;
  videos?: unknown;
} | null | undefined): string {
  const media = extractChatMedia(source);
  const image = media.find((m) => m.type === 'image');
  return image?.url || '';
}

/** Vérifie si la source contient au moins un média (image ou vidéo). */
export function hasChatMedia(source: {
  image?: unknown;
  media?: unknown;
  videos?: unknown;
} | null | undefined): boolean {
  return extractChatMedia(source).length > 0;
}